import { useForgotPasswordMutation } from "@/api/auth";
import { FieldInfo } from "@/components/FieldInfo";
import { Button } from "@/components/ui/Button";
import { ButtonLink } from "@/components/ui/Link";
import { Tooltip } from "@/components/ui/Tooltip";
import { EnvironmentBadge } from "@/components/EnvironmentBadge";
import { GivenwellLogo } from "@/icons/givenwell";
import { config } from "@common/utils/config";
import { ApiErrorResponse } from "@common/api/api-types";
import { makeWrappableEmail } from "@common/utils/string";
import { Box, Field, Flex, Form, Heading, Input, Span, Text } from "@givenwell/components";
import { ChevronLeftIcon } from "@givenwell/core-solid-icons";
import { ForgotPasswordRequest } from "@givenwell/marketplace-api";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useForm } from "@tanstack/react-form";
import { createFileRoute } from "@tanstack/react-router";
import { useId, useLayoutEffect, useState } from "react";

export const Route = createFileRoute("/forgot-password")({
  head: () => ({
    meta: [
      {
        name: "robots",
        content: "noindex",
      },
    ],
  }),
  component: Page,
});

function Page() {
  const mutation = useForgotPasswordMutation();
  const [alternateEmailAddressUsed, setAlternateEmailAddressUsed] = useState<string | undefined>(undefined);

  const form = useForm({
    defaultValues: {
      email_address: "",
    } satisfies ForgotPasswordRequest,
    validators: {
      onSubmitAsync: async values => {
        try {
          const response = await mutation.mutateAsync({ email_address: values.value.email_address });
          setAlternateEmailAddressUsed(response.alternate_email_address ?? undefined);
          return;
        } catch (error) {
          // Capture the error. Assumed to be an ApiErrorResponse.
          const apiError = error as ApiErrorResponse;

          if (apiError?.status === 404) {
            return {
              fields: {
                email_address: "Sorry, we couldn't find a member account with that email address.",
              },
            };
          }

          return {
            form: <>An unexpected error occurred. Please try again later.</>,
          };
        }
      },
    },
  });

  const id = useId();

  useLayoutEffect(() => {
    document.body.style.backgroundColor = colors.warmGrey;
    return () => {
      document.body.style.removeProperty("background-color");
    };
  }, []);

  return (
    <Box
      css={{
        py: 16,
        display: "flex",
        minHeight: "100%",
        flexDirection: "column",
        backgroundColor: colors.cream,
        "@sm": { backgroundColor: "transparent" },
      }}
    >
      <Box css={{ display: "none", "@sm": { display: "block" } }}>
        <a href="https://givenwell.com" style={{ position: "relative" }}>
          <GivenwellLogo css={{ height: 40, mx: 40 }} />
          <EnvironmentBadge
            environmentName={config.environmentName}
            isDev={import.meta.env.DEV}
            position={{ left: 140, top: -5 }}
          />
        </a>
      </Box>
      <Box css={{ flex: "1 0 0px", display: "none", "@sm": { display: "block" } }} />
      <Flex css={{ justify: "center", overflow: "hidden", pb: 32 + 16 }}>
        <Box
          css={{
            width: "100%",
            maxWidth: 600,
            mx: "auto",
            position: "relative",
            borderRadius: 24,
            pt: 32,
            pb: 12,
            minHeight: 600,
            backgroundColor: colors.cream,
            "@sm": {
              boxShadow: "0px 16.0px 16.0px hsl(0deg 0% 0% / 0.05)",
            },
          }}
        >
          <Box
            css={{
              width: "fit-content",
              height: "100%",
              margin: "auto",
              display: "flex",
              flexDirection: "column",
              gap: 24,
              py: 30,
              px: 20,
              "@sm": {
                maxWidth: 450,
              },
            }}
          >
            <Heading as="h1" css={{ fontScale: "3xl", weight: 300, color: colors.navy, fontFamily: fonts.ftHabit }}>
              Forgot Password
            </Heading>
            {mutation.isSuccess ? (
              <>
                <Box css={{ spaceY: 16, borderRadius: 16, padding: 16, backgroundColor: colors.blue100 }}>
                  <Heading css={{ fontScale: "lg", color: colors.navy }}>Check your email</Heading>
                  <Text>
                    We're sending a reset link to
                    {alternateEmailAddressUsed ? <> the email address that looks like the one below: </> : ":"}
                  </Text>
                  <Flex
                    css={{
                      flexDirection: "column",
                      alignItems: "center",
                      backgroundColor: alternateEmailAddressUsed ? colors.yellow700 : colors.white500,
                      color: alternateEmailAddressUsed ? colors.white : "unset",
                      border: alternateEmailAddressUsed ? "none" : `1px dotted ${colors.navy}`,
                      width: "100%",
                      padding: "8px 8px",
                      rounded: 8,
                      margin: "8px auto",
                      overflow: "hidden",
                    }}
                  >
                    <Text
                      css={{
                        fontFamily: "monospace",
                        fontWeight: 500,
                        fontSize: 15,
                        overflowWrap: "break-word",
                        wordBreak: "break-word",
                      }}
                    >
                      {makeWrappableEmail(alternateEmailAddressUsed || form.getFieldValue("email_address"))}
                    </Text>
                    {alternateEmailAddressUsed && (
                      <>
                        <Hr />
                        <Span
                          css={{
                            whiteSpace: "nowrap",
                            fontScale: "sm",
                            opacity: 0.8,
                            pr: 8,
                            alignItems: "center",
                          }}
                        >
                          <Span css={{ fontScale: "md" }}>✨</Span> Reminder: Use this email to sign in
                          <Span css={{ "@maxSm": { display: "none" } }}>to Givenwell.</Span>
                        </Span>
                      </>
                    )}
                  </Flex>{" "}
                  {alternateEmailAddressUsed && (
                    <Tooltip content="We've intentionally hidden part of your email to help keep your information secure.">
                      <Text
                        css={{
                          color: colors.blue500,
                          textDecoration: "dotted underline",
                          whiteSpace: "nowrap",
                          fontScale: "xs",
                          textAlign: "right",
                          mt: -4,
                          mr: 8,
                        }}
                        onTouchStart={() =>
                          alert("We've intentionally hidden part of your email to help keep your information secure.")
                        }
                      >
                        what does *** mean?
                      </Text>
                    </Tooltip>
                  )}
                  <Text>Be sure to check your spam folder if it doesn’t arrive soon.</Text>
                  <Text css={{ fontWeight: 500 }}></Text>
                  {alternateEmailAddressUsed && (
                    <Text>You entered a different email, but we’ve matched it to the one you use to sign in.</Text>
                  )}
                </Box>
                <StillStuck />
                <Box css={{ flex: "1 0 0px" }} />
                <ButtonLink
                  to="/sign-in"
                  variant="plain"
                  size="lg"
                  css={{ width: "100%" }}
                  leftIcon={<ChevronLeftIcon />}
                >
                  Return to Sign In
                </ButtonLink>
              </>
            ) : (
              <Form
                onSubmit={form.handleSubmit}
                id={id}
                css={{ spaceY: 16, display: "flex", flexDirection: "column", flex: "1 0 0px" }}
              >
                <Text css={{ color: colors.text2 }}>
                  Enter your personal or work email address to search for your account.
                </Text>
                <form.Field name="email_address">
                  {field => (
                    <Field>
                      <Input
                        size="lg"
                        required
                        autoFocus
                        type="email"
                        autoComplete="email"
                        placeholder="name@email.com"
                        value={field.state.value}
                        onValueChange={field.handleChange}
                        css={{ background: colors.white, fontSize: 16 }}
                      />
                      <FieldInfo field={field} />
                    </Field>
                  )}
                </form.Field>
                <Box css={{ flex: "1 0 0px" }} />
                <Button
                  type="submit"
                  variant="filled"
                  color="primary"
                  loading={mutation.isPending}
                  form={id}
                  size="lg"
                  css={{ width: "100%" }}
                >
                  Continue
                </Button>
                <ButtonLink
                  to="/sign-in"
                  variant="plain"
                  color="primary"
                  size="lg"
                  css={{ width: "100%" }}
                  leftIcon={<ChevronLeftIcon />}
                >
                  Back to Login
                </ButtonLink>
                <StillStuck />
              </Form>
            )}
          </Box>
        </Box>
      </Flex>
      <Box css={{ flex: "1 0 0px", display: "none", "@sm": { display: "block" } }} />
    </Box>
  );
}

function StillStuck() {
  return (
    <Text css={{ textAlign: "center", fontScale: "xs", color: colors.text2 }}>
      Stuck? Email our friendly team at <A href="mailto:support@givenwell.com">support@givenwell.com</A>
    </Text>
  );
}

const Hr = styled("hr", { width: "100%", height: 0, borderTop: "solid 1px rgba(255, 255, 255, .3)", mx: 8, my: 4 });
const A = styled("a", {
  color: colors.blue600,
  fontWeight: 500,
  textDecoration: "underline",
  "&:hover": { textDecoration: "hover" },
});
